'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import WhatsAppChatLink from '@/components/whatsapp/whatsapp-chat-link'

const HIDDEN_PREFIXES = ['/admin']

/**
 * Floating WhatsApp button (bottom right) on every public page. It hides while the visitor
 * is typing in a form so it never covers a submit button on mobile.
 */
export default function WhatsAppFloat() {
  const pathname = usePathname() || '/'
  const [formFocused, setFormFocused] = useState(false)

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null
      setFormFocused(Boolean(target?.closest('form')))
    }
    const onFocusOut = () => setFormFocused(false)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  if (HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null

  const isEnglish = pathname === '/en' || pathname.startsWith('/en/')

  return (
    <WhatsAppChatLink
      ariaLabel={isEnglish ? 'Chat on WhatsApp' : 'Escríbenos por WhatsApp'}
      className={`fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 transition-all hover:scale-105 hover:bg-[#1ebe5a] md:bottom-6 md:right-6 md:h-14 md:w-14 ${
        formFocused ? 'pointer-events-none translate-y-4 opacity-0 md:pointer-events-auto md:translate-y-0 md:opacity-100' : 'opacity-100'
      }`}
    >
      <svg className="h-6 w-6 md:h-7 md:w-7" viewBox="0 0 448 512" fill="currentColor" aria-hidden="true">
        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zM223.9 436.8c-33.1 0-65.5-8.9-94-25.7l-6.7-4-69.8 18.3L72 357.7l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.2-186.6 184.2zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-2.1-3.6.4-5.2 2.7-7.8 2.6-3 5.4-6 8-8.8 2.8-3 3.8-5.1 5.6-8.5 1.9-3.7 1-7-.4-9.8-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
      </svg>
    </WhatsAppChatLink>
  )
}
